/** WikiAutocompleteTextarea: a native <textarea> that pops a wiki
 *  [[EntityName]] autocomplete. Type `[[`, a debounced popover shows
 *  up to 8 matches from /api/wiki-link/suggest; Arrow keys highlight,
 *  Enter / click inserts `[[Chosen Name]]`, Escape dismisses. When
 *  nothing matches, a "Create new" affordance posts a dotted
 *  (confirmed=0) placeholder graph_node and inserts it.
 *
 *  Caret-anchored via the mirror-div technique (no third-party dep).
 *  Suggestions cached in-module for 60s. No emoji: lucide icons. */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import {
  User,
  Building2,
  MapPin,
  Briefcase,
  Hash,
  Loader2,
  Plus,
} from "lucide-react";
import { api, type WikiCandidate } from "../lib/api";

const ICON: Record<string, typeof User> = {
  person: User,
  org: Building2,
  place: MapPin,
  project: Briefcase,
  topic: Hash,
};
const LABEL: Record<string, string> = {
  person: "Person",
  org: "Org",
  place: "Place",
  project: "Project",
  topic: "Topic",
  engagement: "Engagement",
};
const CREATE_KINDS = ["person", "project", "topic"] as const;

const cache = new Map<string, { ts: number; rows: WikiCandidate[] }>();
const CACHE_MS = 60_000;

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  className?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
}

/** Find an open `[[` immediately left of the caret with no closing
 *  `]]`, newline, or `[[` in between. Returns the query + the index
 *  of the first `[[` char, or null. */
function activeQuery(text: string, caret: number): { q: string; start: number } | null {
  const before = text.slice(0, caret);
  const open = before.lastIndexOf("[[");
  if (open === -1) return null;
  const frag = before.slice(open + 2);
  if (frag.includes("]]") || frag.includes("\n") || frag.includes("[[")) return null;
  if (frag.length > 60) return null;
  return { q: frag, start: open };
}

export function WikiAutocompleteTextarea({
  value,
  onChange,
  placeholder,
  rows = 3,
  maxLength,
  className,
  onKeyDown,
}: Props) {
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [queryStart, setQueryStart] = useState(0);
  const [rowsData, setRowsData] = useState<WikiCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [creating, setCreating] = useState(false);

  const measureCaret = useCallback((caretIdx: number) => {
    const ta = taRef.current;
    const mirror = mirrorRef.current;
    if (!ta || !mirror) return;
    const cs = window.getComputedStyle(ta);
    for (const p of [
      "fontFamily",
      "fontSize",
      "fontWeight",
      "lineHeight",
      "letterSpacing",
      "paddingTop",
      "paddingRight",
      "paddingBottom",
      "paddingLeft",
      "borderTopWidth",
      "borderLeftWidth",
      "borderRightWidth",
      "borderBottomWidth",
      "boxSizing",
    ] as const) {
      mirror.style[p as never] = cs[p as never];
    }
    mirror.style.width = `${ta.clientWidth}px`;
    mirror.style.whiteSpace = "pre-wrap";
    mirror.style.wordWrap = "break-word";
    mirror.style.position = "absolute";
    mirror.style.visibility = "hidden";
    mirror.style.top = "0";
    mirror.style.left = "0";
    mirror.textContent = value.slice(0, caretIdx);
    const marker = document.createElement("span");
    marker.textContent = "​";
    mirror.appendChild(marker);
    const taRect = ta.getBoundingClientRect();
    const top =
      taRect.top +
      window.scrollY +
      marker.offsetTop -
      ta.scrollTop +
      (parseFloat(cs.lineHeight) || 18);
    const left =
      taRect.left + window.scrollX + Math.min(marker.offsetLeft, ta.clientWidth - 40);
    setPos({ top, left });
  }, [value]);

  const runQuery = useCallback((q: string) => {
    const key = q.toLowerCase();
    const hit = cache.get(key);
    if (hit && Date.now() - hit.ts < CACHE_MS) {
      setRowsData(hit.rows);
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .wikiSuggest(q)
      .then((d) => {
        cache.set(key, { ts: Date.now(), rows: d.suggestions });
        setRowsData(d.suggestions);
      })
      .catch(() => setRowsData([]))
      .finally(() => setLoading(false));
  }, []);

  const sync = useCallback(
    (text: string, caret: number) => {
      const found = activeQuery(text, caret);
      if (!found) {
        setOpen(false);
        return;
      }
      setOpen(true);
      setQuery(found.q);
      setQueryStart(found.start);
      setHi(0);
      measureCaret(caret);
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      if (found.q.length === 0) {
        setRowsData([]);
        setLoading(false);
        return;
      }
      debounceRef.current = window.setTimeout(() => runQuery(found.q), 200);
    },
    [measureCaret, runQuery],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, []);

  const insert = useCallback(
    (name: string) => {
      const ta = taRef.current;
      const caret = ta ? ta.selectionStart : value.length;
      const next =
        value.slice(0, queryStart) + `[[${name}]]` + value.slice(caret);
      onChange(next);
      setOpen(false);
      requestAnimationFrame(() => {
        if (!ta) return;
        const cpos = queryStart + name.length + 4;
        ta.focus();
        ta.setSelectionRange(cpos, cpos);
      });
    },
    [value, queryStart, onChange],
  );

  const doCreate = useCallback(
    async (kind: string) => {
      if (!query.trim() || creating) return;
      setCreating(true);
      try {
        const r = await api.wikiCreatePlaceholder(query.trim(), kind);
        insert(r.display_name);
      } catch {
        /* leave popover open on failure */
      } finally {
        setCreating(false);
      }
    },
    [query, creating, insert],
  );

  // Selectable rows = suggestions then (always) a Create row.
  const optionCount = rowsData.length + 1;

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHi((h) => (h + 1) % optionCount);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHi((h) => (h - 1 + optionCount) % optionCount);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key === "Enter" && !e.metaKey && !e.ctrlKey) {
        if (hi < rowsData.length) {
          e.preventDefault();
          insert(rowsData[hi].display_name);
          return;
        }
        if (hi === rowsData.length && query.trim()) {
          e.preventDefault();
          void doCreate("topic");
          return;
        }
      }
    }
    onKeyDown?.(e);
  };

  const popover = useMemo(() => {
    if (!open) return null;
    return (
      <div
        className="fixed z-[60] w-72 max-h-72 overflow-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl text-sm"
        style={{ top: pos.top, left: pos.left }}
        onMouseDown={(e) => e.preventDefault()}
      >
        {loading && (
          <div className="flex items-center gap-2 px-3 py-2 text-slate-500 dark:text-slate-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching...
          </div>
        )}
        {!loading &&
          rowsData.map((s, i) => {
            const Icon = ICON[s.kind] ?? Hash;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => insert(s.display_name)}
                onMouseEnter={() => setHi(i)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left ${
                  hi === i
                    ? "bg-violet-600 text-white"
                    : "text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                <Icon className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate flex-1">{s.display_name}</span>
                <span
                  className={`text-[10px] uppercase tracking-wider ${
                    hi === i ? "text-white/80" : "text-slate-400"
                  }`}
                >
                  {LABEL[s.kind] ?? s.kind}
                </span>
              </button>
            );
          })}
        {!loading && query.trim() && (
          <div
            className={`border-t border-slate-200 dark:border-slate-700 px-3 py-2 ${
              hi === rowsData.length ? "bg-violet-50 dark:bg-violet-900/20" : ""
            }`}
          >
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              <Plus className="w-3 h-3" /> Create new for "{query.trim()}"
            </div>
            <div className="mt-1 flex gap-1">
              {CREATE_KINDS.map((k) => {
                const Icon = ICON[k] ?? Hash;
                return (
                  <button
                    key={k}
                    type="button"
                    disabled={creating}
                    onClick={() => void doCreate(k)}
                    className="inline-flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 text-[11px] font-semibold text-slate-700 dark:text-slate-200 disabled:opacity-50"
                  >
                    <Icon className="w-3 h-3" /> {LABEL[k]}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {!loading && rowsData.length === 0 && !query.trim() && (
          <div className="px-3 py-2 text-slate-400">Keep typing...</div>
        )}
      </div>
    );
  }, [open, pos, loading, rowsData, hi, query, creating, insert, doCreate]);

  return (
    <>
      <textarea
        ref={taRef}
        rows={rows}
        maxLength={maxLength}
        value={value}
        placeholder={placeholder}
        className={
          className ??
          "mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-900/40 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
        }
        onChange={(e) => {
          onChange(e.target.value);
          sync(e.target.value, e.target.selectionStart);
        }}
        onClick={(e) => sync(value, e.currentTarget.selectionStart)}
        onKeyUp={(e) => {
          if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
            sync(value, e.currentTarget.selectionStart);
          }
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
      />
      <div ref={mirrorRef} aria-hidden="true" />
      {popover}
    </>
  );
}

export default WikiAutocompleteTextarea;
